import { motion } from "framer-motion";
import { Quote, ArrowRight } from "lucide-react";
import { SectionHeader } from "./Services";

export default function CaseStudy() {
    return (
        <section
            id="case-study"
            className="relative py-28 bg-[#040A18] border-y border-white/5"
            data-testid="case-study-section"
        >
            <div className="pr-container">
                <SectionHeader
                    overline="Case study"
                    title="A 30% improvement in brand recall — measured, not estimated"
                    subtitle="Snapshot of a recent CATI program for a multinational consumer brand."
                />

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="mt-14 pr-glass-strong rounded-3xl p-8 sm:p-10 relative overflow-hidden"
                >
                    <div className="absolute -top-24 -right-20 w-80 h-80 bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative grid lg:grid-cols-3 gap-10">
                        <Block
                            label="Problem"
                            text="A consumer brand had stagnant brand recall across two key markets despite high media spend. They needed validated human signal to redirect budget."
                        />
                        <Block
                            label="Solution"
                            text="We fielded a 4,800-respondent CATI program with a custom recall battery, voice-audited for quality and weighted to census demographics."
                        />
                        <Block
                            label="Result"
                            text="Reallocated spend per Proksy insights drove a measurable +30% lift in aided recall and a 14% rise in trial intent within one quarter."
                            highlight
                        />
                    </div>

                    <div className="relative mt-10 grid lg:grid-cols-3 gap-10">
                        <div className="lg:col-span-2">
                            <div className="flex items-center gap-3 text-slate-300">
                                <Quote className="w-5 h-5 text-blue-400" />
                                <p className="font-outfit italic text-base leading-relaxed">
                                    "Proksy gave us numbers our CFO believed —
                                    and a story our CMO could act on."
                                </p>
                            </div>
                            <p className="mt-3 ml-8 text-xs font-mono-pr text-slate-500">
                                — Head of Insights, Global FMCG
                            </p>
                        </div>

                        <div>
                            <div className="flex items-center justify-between text-xs font-mono-pr text-slate-400">
                                <span>BRAND RECALL · LIFT</span>
                                <span className="text-emerald-300">+30%</span>
                            </div>
                            <div className="mt-2 h-3 bg-white/5 rounded-full overflow-hidden border border-white/5">
                                <motion.div
                                    initial={{ width: 0 }}
                                    whileInView={{ width: "78%" }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 1.6,
                                        ease: "easeOut",
                                    }}
                                    className="h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 shadow-[0_0_18px_rgba(34,197,94,0.6)]"
                                />
                            </div>
                            <div className="mt-4 flex items-center justify-between text-xs font-mono-pr text-slate-400">
                                <span>TRIAL INTENT · LIFT</span>
                                <span className="text-blue-300">+14%</span>
                            </div>
                            <div className="mt-2 h-3 bg-white/5 rounded-full overflow-hidden border border-white/5">
                                <motion.div
                                    initial={{ width: 0 }}
                                    whileInView={{ width: "52%" }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 1.6,
                                        delay: 0.3,
                                        ease: "easeOut",
                                    }}
                                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 shadow-[0_0_18px_rgba(59,130,246,0.5)]"
                                />
                            </div>
                            <a
                                href="#lead-form"
                                className="mt-6 inline-flex items-center gap-2 text-sm font-outfit font-semibold text-blue-300 hover:text-blue-200"
                                data-testid="case-study-cta"
                            >
                                Run a similar study
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function Block({ label, text, highlight }) {
    return (
        <div
            className={`rounded-2xl p-6 border ${
                highlight
                    ? "bg-emerald-500/5 border-emerald-400/30"
                    : "bg-white/[0.02] border-white/10"
            }`}
        >
            <p
                className={`font-outfit text-xs uppercase tracking-[0.25em] ${
                    highlight ? "text-emerald-300" : "text-blue-400"
                } font-bold`}
            >
                {label}
            </p>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                {text}
            </p>
        </div>
    );
}
